import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Calendar, Clock, Tag } from 'lucide-react';
import { formatDuration } from '../../../../utils/formatDuration';

const BannerEstreno = ({ shows = [] }) => {
  const [currentShowIndex, setCurrentShowIndex] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  // rotación automática
  useEffect(() => {
    if (!shows.length) return;
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setIsChanging(true);
      setTimeout(() => {
        setCurrentShowIndex(prev => (prev === shows.length - 1 ? 0 : prev + 1));
        setTimeout(() => setIsChanging(false), 50);
      }, 500);
    }, 10000);

    return () => timerRef.current && clearInterval(timerRef.current);
  }, [shows]);

  if (!shows.length) {
    return (
      <div className="w-full h-[40vh] sm:h-[45vh] md:h-[50vh] lg:h-[55vh] bg-gradient-to-r from-gray-900 to-gray-800" />
    );
  }

  const currentShow = shows[currentShowIndex];

  const truncateText = (text, maxLength) =>
    !text ? '' : (text.length > maxLength ? text.substring(0, maxLength) + '...' : text);

  const adjustColor = (hex, amount) => {
    hex = (hex || '').replace('#', '');
    if (hex.length !== 6) return '#000000';
    let r = Math.max(0, Math.min(255, parseInt(hex.slice(0,2),16) + amount));
    let g = Math.max(0, Math.min(255, parseInt(hex.slice(2,4),16) + amount));
    let b = Math.max(0, Math.min(255, parseInt(hex.slice(4,6),16) + amount));
    return `#${((1<<24) + (r<<16) + (g<<8) + b).toString(16).slice(1)}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
  };

  const maxDesc = window.innerWidth < 640 ? 150 : window.innerWidth < 768 ? 250 : 500;

  return (
    <div className="relative w-full h-[50vh] sm:h-[55vh] md:h-[60vh] lg:h-[70vh] mx-auto bg-black overflow-hidden shadow-2xl rounded-lg">
      <div 
        className={`absolute inset-0 transition-opacity duration-500 ${isChanging ? 'opacity-0' : 'opacity-100'}`} 
        style={{
          backgroundImage: `url(${currentShow.imagen})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 25%',
          filter: 'brightness(0.7)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />

      <div className={`absolute inset-0 flex flex-col justify-center p-4 sm:p-6 md:p-10 lg:p-16 transition-all duration-500 ${isChanging ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
        <div className="inline-flex items-center bg-red-600 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-white text-xs font-medium mb-2 sm:mb-4 max-w-max">
          ESTRENO
        </div>

        {currentShow.comediantes?.length > 0 && (
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold max-w-2xl drop-shadow-lg text-white">
            {currentShow.comediantes.join(', ')}
          </h2>
        )}

        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold max-w-2xl drop-shadow-lg mb-2"
            style={{ color: "#f08800" }}>
          {currentShow.titulo}
        </h1>

        {currentShow.descripcion && (
          <p className="text-white text-sm sm:text-base md:text-lg max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl hidden xs:line-clamp-2 sm:line-clamp-3 md:line-clamp-4">
            {truncateText(currentShow.descripcion, maxDesc)}
          </p>
        )}

        <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 sm:mt-4 items-center text-gray-200 text-xs sm:text-sm">
          <div className="flex items-center">
            <Calendar size={14} className="mr-1 sm:mr-2" />
            <span>{formatDate(currentShow.fechaEstreno)}</span>
          </div>
          <div className="flex items-center">
            <Clock size={14} className="mr-1 sm:mr-2" />
            <span>{formatDuration(currentShow.duracion)} min</span>
          </div>
        </div>

        {currentShow.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 sm:gap-2 mt-2">
            <Tag size={14} className="mr-1 text-gray-200" />
            {currentShow.tags.slice(0, window.innerWidth < 640 ? 1 : window.innerWidth < 768 ? 2 : 3).map((tag, i) => {
              const color = tag.colorHex || '#EF4444';
              return (
                <div key={tag.tagId || i}
                     className="text-white text-xs font-bold px-2 py-1 rounded-full shadow-md border"
                     style={{ backgroundColor: color, borderColor: adjustColor(color, -20) }}>
                  {tag.nombre}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 sm:mt-5 md:mt-8">
          <button
            onClick={() => currentShow.videoShowId && navigate(`/contenido/ver/${currentShow.videoShowId}`)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-full inline-flex items-center gap-2 text-xs sm:text-sm md:text-base font-medium transition-all duration-300 shadow-lg hover:shadow-red-700/30">
            <Play size={16} />
            <span>Reproducir</span>
          </button>
        </div>
      </div>

      <div className="absolute bottom-3 sm:bottom-5 left-4 sm:left-10 flex space-x-2">
        {shows.map((_, idx) => (
          <button key={idx}
            className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${idx === currentShowIndex ? 'w-4 sm:w-6 md:w-8 bg-red-600' : 'w-1.5 sm:w-2 bg-gray-500 hover:bg-gray-300'}`}
            onClick={() => setCurrentShowIndex(idx)}
            aria-label={`Ver estreno ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default BannerEstreno;